import { GridColDef, isAutogeneratedRow } from "@mui/x-data-grid-premium";
import PaymentStatusChip from "./PaymentStatusChip";
import { formatCurrencyAmount } from "../../utils/formatters";
import { formatDateToDDMMYYYY } from "../../utils/dateFormetter";
import { withCaseInsensitiveStringFilters } from "../../utils/stringFilterOperators";
import { getPaymentStatusOptions } from "../../constants/statusFilterOptions";
import AssignedUserChip from "../documents/AssignedUserChip";
import { PaymentDocumentDTO } from "../../types/PaymentDocumentDTO";
import { OwnerOption } from "../documents/columns";

export const createPaymentColumns = (
  t: (key: string, options?: any, ns?: any) => string,
  ownerOptions?: OwnerOption[]
): GridColDef<PaymentDocumentDTO>[] => [
  withCaseInsensitiveStringFilters({
    field: "documentNo",
    headerName: t("documentNo"),
    width: 150,
    filterable: true,
    sortable: true,
    aggregable: false,
    valueFormatter: (value) => value || "",
  }) as GridColDef<PaymentDocumentDTO>,

  withCaseInsensitiveStringFilters({
    field: "billToPayToName",
    headerName: t("billToPayToName"),
    flex: 1,
    filterable: true,
    sortable: true,
    aggregable: false,
    valueFormatter: (value) => value || "",
  }) as GridColDef<PaymentDocumentDTO>,

  {
    field: "paymentStatus",
    headerName: t("paymentStatus"),
    type: "singleSelect",
    flex: 1,
    sortable: true,
    filterable: true,
    aggregable: false,
    valueOptions: getPaymentStatusOptions(t),

    valueGetter: (_value, row) =>
      isAutogeneratedRow(row)
        ? undefined
        : row.paymentStatus || "__EMPTY__",

    valueFormatter: (value) => {
      if (!value) {
        return "";
      }

      return value === "__EMPTY__"
        ? t("Do zapłaty")
        : t(value, {}, "common");
    },

    renderCell: (params) =>
      isAutogeneratedRow(params.row) ? null : (
        <PaymentStatusChip status={params.value} />
      ),
  },

  {
    field: "amountInclVATDecimal",
    headerName: t("amountInclVATDecimal"),
    type: "number",
    width: 120,
    filterable: true,
    sortable: true,
    aggregable: true,
    availableAggregationFunctions: ["sum", "avg", "min", "max"],
    align: "right",
    headerAlign: "right",

    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.amountInclVATDecimal,

    valueFormatter: (value) =>
      value === undefined || value === null
        ? ""
        : formatCurrencyAmount(value),
  },

  {
    field: "remainingAmount",
    headerName: t("remainingAmount"),
    type: "number",
    width: 120,
    filterable: true,
    sortable: true,
    aggregable: true,
    availableAggregationFunctions: ["sum", "avg", "min", "max"],
    align: "right",
    headerAlign: "right",

    valueGetter: (_value, row) =>
      isAutogeneratedRow(row) ? undefined : row.remainingAmount,

    valueFormatter: (value) =>
      value === undefined || value === null
        ? ""
        : formatCurrencyAmount(value),
  },

  withCaseInsensitiveStringFilters({
    field: "currencyCode",
    headerName: t("currency"),
    filterable: true,
    sortable: true,
    aggregable: false,
    valueFormatter: (value) => value || "",
    renderCell: (params) =>
      isAutogeneratedRow(params.row) ? null : (
        <>{params.row.currencyCode || ""}</>
      ),
  }) as GridColDef<PaymentDocumentDTO>,

  {
    field: "dueDate",
    headerName: t("dueDate"),
    type: "date",
    width: 150,
    filterable: true,
    sortable: true,
    aggregable: false,

    valueGetter: (value, row) => {
      if (isAutogeneratedRow(row)) {
        return null;
      }

      if (!value || String(value).startsWith("0001")) {
        return null;
      }

      return new Date(value);
    },

    valueFormatter: (value) => {
      if (!value) {
        return "—";
      }

      return formatDateToDDMMYYYY(value);
    },
  },

  {
    field: "assignedUserEmail",
    headerName: t("assignedUserEmail"),
    type: "singleSelect",
    sortable: true,
    filterable: true,
    aggregable: false,
    flex: 0.9,
    minWidth: 160,
    valueOptions: ownerOptions ?? [],
    valueFormatter: (value) => value || "-",
    renderCell: (params) =>
      isAutogeneratedRow(params.row) ? null : (
        <AssignedUserChip
          email={params.row.assignedUserEmail}
          displayName={params.row.assignedUserDisplayName}
        />
      ),
  },
];